/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, { useEffect } from 'react';

import { cssMap as cssMapCompiled, cx } from '@compiled/react';
import { type IntlShape, useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import { IconButton } from '@atlaskit/button/new';
import { jsx } from '@atlaskit/css';
import { ProviderFactory, type Providers } from '@atlaskit/editor-common/provider-factory';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import Link from '@atlaskit/link';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import ProfileCardClient from '@atlaskit/profilecard/client';
import Suspense from '@atlaskit/react-ufo/suspense';
import { ReactRenderer } from '@atlaskit/renderer';
import type { RenderOutputStat } from '@atlaskit/renderer/render-document';
import { Card } from '@atlaskit/smart-card';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import { metrics } from '@atlassian/browser-metrics';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { hydrateLinkingPlatformCache } from '@atlassian/conversation-assistant-content-transformer/linking-platform';
import {
	type FollowUpSegment,
	isActionableFollowUpsStatus,
	parseActionableFollowUps,
} from '@atlassian/conversation-assistant-content-transformer/utils/actionable-follow-ups';
import { isInlineLuminaStatus } from '@atlassian/conversation-assistant-content-transformer/utils/inline-lumina';
import {
	isSkillTagExtension,
	ROVO_SKILL_COMBINED_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { DashboardInsightsExpandable } from '@atlassian/conversation-assistant-ui-components/dashboard-insights-expandable';
import { ChartError } from '@atlassian/conversation-assistant-viz/ui/visualization-chart/ChartError';
import { ChartLoader } from '@atlassian/conversation-assistant-viz/ui/visualization-chart/ChartLoader';
import { WidgetWrapper } from '@atlassian/conversation-assistant-viz/ui/widget-wrapper';
import {
	VISUALIZATION_CHART_PROP_SCHEMA,
	type StreamableChartWidgetProps,
} from '@atlassian/conversation-assistant-viz/ui/widget-wrapper/chart-prop-schema';
import type { LuminaInteractionHandler } from '@atlassian/lumina/interaction';
import { type Skill } from '@atlassian/rovo-platform-types/skills';
import { ActionableFollowUpLink } from '@atlassian/rovo-platform-ui-components/actionable-follow-up-link';
import { SkillTag } from '@atlassian/rovo-platform-ui-components/skills/SkillTag';
import { SmartMentionResource } from '@atlassian/smart-mention-resource';
import { UserProfileCardForMention } from '@atlassian/user-profile-card/mention';

import {
	handleDeepLink,
	handleOnSetLinkTarget,
	shouldHandleDeepLink,
} from '../../../utils/deep-links';
import { useRovoMentionProvider } from '../../../utils/mention-provider';
import { useRovoProfilecardProvider } from '../../../utils/profilecard-provider';
import { transformIfConfluenceLink } from '../../../utils/url-transformer';
import { StatusCitation } from '../status-citation';

import { MentionNodeComponent } from './create-mention-node-component';
import { MentionNodeComponentProvider } from './mention-node-component-provider';
import messages from './messages';

export type ExtensionComponentProps = {
	content?: React.ReactNode;
	extensionType?: string;
	extensionKey?: string;
	parameters?: Record<string, unknown>;
};

type EmbedCardErrorInfo = {
	err?: Error;
	status: 'errored' | 'fallback' | 'unauthorized' | 'forbidden' | 'not_found';
	url: string;
};

// Component to handle embed card with link fallback
const EmbedCardWithFallback = ({
	url,
	onResolutionError,
}: {
	url?: string;
	onResolutionError?: (url: string | undefined, errorInfo: EmbedCardErrorInfo) => void;
}) => {
	return (
		<Card
			appearance="embed"
			frameStyle="show"
			url={url}
			onError={(errorInfo: EmbedCardErrorInfo) => {
				onResolutionError?.(url, errorInfo);
			}}
			fallbackComponent={() => (
				<Link href={url ?? ''} target="_blank">
					{url}
				</Link>
			)}
		/>
	);
};

// Lazy load ReactMarkdown for expandable markdown support
const LazyReactMarkdown = React.lazy(
	() => import(/* webpackChunkName: "react-markdown" */ 'react-markdown'),
);
const VisualizationChart = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant-content-renderer/viz-chart" */ '@atlassian/conversation-assistant-viz'
	).then((module) => ({
		default: module.VisualizationChart,
	})),
);

//Styles overrides for content renderer
/* eslint-disable @atlaskit/design-system/no-unsafe-design-token-usage, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-important-styles */
const stylesOverrides = cssMapCompiled({
	inverse: {
		'--ds-text': token('color.text.inverse'),

		/*Profile card styling*/
		'& [role="dialog"]': {
			'--ds-text': token('color.text'),
			color: token('color.text'),
		},
		/*Code block styling*/
		'& .code-block, & [data-ds--code--code-block]': {
			'--ds-text': token('color.text'),
		},
		/*Regular link styling*/
		'a:not([data-inline-card] a)': {
			color: token('color.text.inverse'),
		},
		/*Failed inline card - plain anchor inside data-inline-card without resolved view*/
		'[data-inline-card] > a': {
			color: token('color.text.inverse'),
		},
		/*Visited link styling*/
		'a:not([data-inline-card] a):visited': {
			color: token('color.background.accent.purple.subtle.hovered'),
		},
		/*Failed inline card visited link*/
		'[data-inline-card] > a:visited': {
			color: token('color.background.accent.purple.subtle.hovered'),
		},
		/*
		 * Mention chips on the inverse chat bubble borrow the people-mention visual
		 * language, in two groups:
		 *   - "Someone else" — default/restricted people AND inactive/disabled agents
		 *     (types 1, 2, 3): the standard translucent inverse pill.
		 *   - "My own mention" — your self-mention AND the active/responding agent (type 0
		 *     or tagged with data-active-agent-mention): emphasised blue, so the responder
		 *     stands out like your own mention does.
		 * Selectors require [data-mention-type] on the chip span so we don't paint the
		 * unqualified tooltip-wrapper span behind a disabled chip (the "square" artifact).
		 */
		'[data-mention-id] a, [data-mention-id] span[data-mention-type="1"], [data-mention-id] span[data-mention-type="2"], [data-mention-id] span[data-mention-type="3"]':
			{
				color: token('color.text.inverse'),
				backgroundColor: token('color.background.inverse.subtle'),
			},
		/*
		 * Self-mention styling. The active-agent selector includes [data-mention-id] so it
		 * out-specifies the type="2" rule above, which the active chip also matches — at
		 * equal specificity the winner depends on stylesheet order and the active agent
		 * rendered like an inactive one.
		 */
		'[data-mention-id] span[data-mention-type="0"], [data-active-agent-mention] [data-mention-id] span[data-mention-type]':
			{
				'--ds-text': token('color.text'),
				color: token('color.text'),
				backgroundColor: token('color.background.accent.blue.subtle'),
			},
		/*Blockquote styling*/
		'blockquote, [data-node-type="blockquote"]': {
			'--ds-border': token('color.border.inverse'),
		},
		/*Inline code styling*/
		code: {
			backgroundColor: token('color.background.inverse.subtle'),
			color: token('color.text.inverse'),
		},
	},
	inverseDarkMode: {
		/*Profile card styling in dark mode*/
		'& [role="dialog"]': {
			'--ds-text': '#BFC1C4',
		},
		/*Code block styling in dark mode*/
		'& .code-block, & [data-ds--code--code-block]': {
			'--ds-text': '#BFC1C4',
		},
		/* "My own mention" look in dark mode — self-mention + active/responding agent. */
		'[data-mention-id] span[data-mention-type="0"], [data-active-agent-mention] [data-mention-id] span[data-mention-type]':
			{
				'--ds-text': '#FFFFFF',
			},
		/*Regular link styling in dark mode - improved contrast against blue chat bubble*/
		'a:not([data-inline-card] a)': {
			color: token('color.text'),
		},
		/*Failed inline card link styling in dark mode*/
		'[data-inline-card] > a': {
			color: token('color.text'),
		},
	},
	subtlestText: {
		'--ds-text': token('color.text.subtlest'),
	},
	boldText: {
		'& p, & h1, & h2, & h3, & h4, & h5, & h6, & li, & span': {
			fontWeight: `${token('font.weight.bold')} !important`,
		},
	},
});
/* eslint-enable */

const skillsStyles = cssMapCompiled({
	container: {
		display: 'inline-flex',
	},
});

const timeToRenderMessageContent = metrics.interaction({
	key: 'ai-mate.time-to-render-message-content',
});

type Content = DocNode | string;

export type ContentRendererProps = {
	content: Content;
	onContentSelected?: () => void;
	onContentCopied?: () => void;
	lightweightEncoding?: boolean;
	onResponseLinkClick?: () => void;
	onSmartCardClick?: () => void;
	onCodeBlockCopied?: () => void;
	allowDownloadCodeBlock?: boolean;
	onCitationClick?: (index: number, citedProduct: string | null, product: string | null) => void;
	onAuthLinkClick?: (url: string, authType: '3p' | 'forge') => void;
	getOnViewAnalysisClick?: (idx: number) => () => void;
	onDeepLinkClick?: (url: string) => void;
	markdownData?: Record<string, string>;
	smallerFont?: boolean;
	largerFont?: boolean;
	boldText?: boolean;
	subtlestText?: boolean;
	productKey: string;
	orgId?: string;
	siteId: string;
	aaid?: string;
	conversationId?: string;
	theme?: 'default' | 'inverse';
	messageId?: string;
	/**
	 * Whether the content is still streaming. This is used to throttle perf tracking across all render cycles to avoid over-reporting with avg render time.
	 * If this is false or undefined, the content is considered to be fully available and the time to render is tracked right away.
	 */
	contentStillStreaming?: boolean;
	/**
	 * Whether the content renderer is in mini-modal view. When true, embed cards will render as inline cards.
	 * When false or undefined, embed cards will render as embed cards.
	 */
	shouldOverrideCardAsInline?: boolean;
	/**
	 * stub for experiment evaluation to pass down to md plugins
	 */
	isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean;
	/**
	 * Optional ADF transformer function to apply after encoding.
	 * Use this to apply custom transformations to the ADF before rendering.
	 */
	adfTransformer?: (adf: DocNode) => DocNode;
	/**
	 * Optional callback to send link count metrics when ADF is ready.
	 * Called with streamingComplete status and the final ADF document.
	 */
	sendLinkCountMetric?: (streamingComplete: boolean, adf: DocNode) => void;
	/**
	 * Optional override for the extension node component.
	 * Use this to provide custom rendering for ADF extension nodes (e.g. insight cards, profile groups).
	 * If not provided, extensions will render their default content passthrough.
	 */
	extensionComponent?: React.ComponentType<ExtensionComponentProps>;
	/**
	 * Optional function to process URLs for cross-product interaction-tracking (XPC).
	 * Called with `stripOnly = true` for links rendered as smart links by the
	 * linking platform (only strips `datasource` param), and `stripOnly = false`
	 * for plain links (strips + applies full XPC URL wrapping).
	 */
	xpcWrapUrl?: (url: string, stripOnly: boolean) => string;
	/**
	 * Whether the current conversation is with a third-party agent.
	 * When true, expand-body content is rendered through the ADF pipeline
	 * (`ReactRenderer` over `encodeAdf(content)`) for full markdown support.
	 */
	tempIsThirdPartyAgent?: boolean;
	/**
	 * Optional callback invoked when an actionable follow-up question is clicked.
	 * Called with the question text so the host can auto-submit it as the next
	 * user message.
	 */
	onFollowUpClick?: (question: string) => void;
	interactionMode?: 'live' | 'read-only';
	/** Optional callback for allowlisted interactions with inline Lumina content. */
	onLuminaInteraction?: LuminaInteractionHandler;
	/**
	 * Id of the agent that actually responded to this turn (`agent_author`).
	 * When provided and the content @mentions one or more agents, every agent
	 * mention whose id differs from this one is rendered greyed-out (disabled)
	 * so the responding agent is visually distinct. If the responding agent was
	 * not @mentioned in the prompt (e.g. failover), no mention matches and all
	 * mentioned agents render disabled. When omitted (e.g. the default agent),
	 * nothing is greyed. Passed by the human-message path.
	 */
	respondedAgentId?: string;
};

type StatusComponentProps = {
	localId: string;
	text: string;
	color?: string;
};

type DataSculptorButtonProps = {
	localId: string;
	text: string;
	formatMessage: IntlShape['formatMessage'];
	getOnViewAnalysisClick: ((idx: number) => () => void) | undefined;
};

const DataSculptorButton = React.memo(
	({ localId, text, formatMessage, getOnViewAnalysisClick }: DataSculptorButtonProps) => {
		return (
			<IconButton
				icon={(iconProps) => <AngleBracketsIcon {...iconProps} color={token('color.icon.brand')} />}
				label={formatMessage(messages.viewCodeButtonLabel)}
				isTooltipDisabled={false}
				appearance="subtle"
				spacing="compact"
				onClick={getOnViewAnalysisClick?.(parseInt(text.at(-1) ?? '0', 10))}
			/>
		);
	},
);

const hideCodeBlockLineNumbers = (document: DocNode): DocNode => {
	const transformedDocument = traverse(document, {
		codeBlock: (node) => ({
			...node,
			attrs: {
				...node.attrs,
				hideLineNumbers: true,
			},
		}),
	});

	return transformedDocument === false ? document : (transformedDocument as DocNode);
};

const ExpandableContent = React.memo(
	({
		localId,
		formatMessage,
		expandDataContent,
		tempIsThirdPartyAgent,
	}: {
		localId: string;
		formatMessage: IntlShape['formatMessage'];
		expandDataContent: string;
		tempIsThirdPartyAgent?: boolean;
	}) => {
		const { header, content } = splitHeaderAndContent(expandDataContent);

		const shouldRenderEnhancedMarkdown = !!tempIsThirdPartyAgent;

		const renderBody = () => {
			if (shouldRenderEnhancedMarkdown) {
				const document = encodeAdf(content, true);
				const shouldHideCodeBlockLineNumbers = FeatureGates.getExperimentValue(
					'jira_ai_expandable_hide_line_num_code_block',
					'isEnabled',
					false,
				);

				return (
					<ReactRenderer
						document={
							shouldHideCodeBlockLineNumbers ? hideCodeBlockLineNumbers(document) : document
						}
						allowCopyToClipboard
						allowWrapCodeBlock={
							FeatureGates.getExperimentValue(
								'jira_ai_agent_code_markdown_wrappable',
								'isEnabled',
								false,
							) || undefined
						}
					/>
				);
			}
			if (fg('rovo-chat-expandable-markdown-support')) {
				return (
					<Suspense
						interactionName="conversation-assistant-content-renderer-markdown-suspense"
						fallback={<div>{formatMessage(messages.loadingMarkdownMessage)}</div>}
					>
						<LazyReactMarkdown>{content}</LazyReactMarkdown>
					</Suspense>
				);
			}
			return <React.Fragment>{content}</React.Fragment>;
		};

		return (
			<DashboardInsightsExpandable title={header} defaultExpanded={false} testId={localId}>
				{renderBody()}
			</DashboardInsightsExpandable>
		);
	},
);

const ChartContent = React.memo(
	({
		localId,
		formatMessage,
		isChartLoading,
		chartConfigJson,
		messageId,
		siteId,
	}: {
		localId: string;
		formatMessage: IntlShape['formatMessage'];
		isChartLoading: boolean;
		chartConfigJson: string;
		messageId: string | undefined;
		siteId?: string;
	}) => {
		if (fg('rovo_chat_enable_updated_chart_loading')) {
			return (
				<Suspense
					interactionName="conversation-assistant-content-renderer-chart-suspense"
					fallback={<Text>{formatMessage(messages.loadingChartMessage)}</Text>}
				>
					<WidgetWrapper<StreamableChartWidgetProps>
						jsonStream={chartConfigJson}
						component={VisualizationChart}
						propSchema={VISUALIZATION_CHART_PROP_SCHEMA}
						extraProps={{
							isChartLoading,
							testId: localId,
							messageId,
							cloudId: siteId,
						}}
					/>
				</Suspense>
			);
		}

		if (isChartLoading || chartConfigJson === '{}') {
			return <ChartLoader testId={localId} interactionName="rovo-viz-chart-loader" />;
		}

		try {
			const chartConfig = JSON.parse(chartConfigJson);

			return (
				<Suspense
					interactionName="conversation-assistant-content-renderer-chart-suspense"
					fallback={<Text>{formatMessage(messages.loadingChartMessage)}</Text>}
				>
					<VisualizationChart
						{...chartConfig}
						testId={localId}
						messageId={messageId}
						cloudId={siteId}
					/>
				</Suspense>
			);
		} catch {
			return <ChartError testId={localId} />;
		}
	},
);

const InlineLuminaContent = React.memo(
	({
		luminaContent,
		cloudId,
		orgId,
		requestingUserId,
		sourceProduct,
		messageId,
		onInteraction,
	}: {
		luminaContent: string;
		cloudId: string;
		orgId: string;
		requestingUserId: string;
		sourceProduct: string;
		messageId?: string;
		onInteraction?: LuminaInteractionHandler;
	}) => {
		if (luminaContent) {
			return (
				<Suspense
					interactionName="conversation-assistant-content-renderer-lumina-suspense"
					fallback={null}
				>
					<SmartCardProvider>
						<LuminaLayout
							luminaTagResponse={`<lumina>${luminaContent}</lumina>`}
							source="luminaRovoChatSidebar"
							cloudId={cloudId}
							orgId={orgId}
							requestingUserId={requestingUserId}
							sourceProduct={sourceProduct}
							sessionId={messageId}
							onInteraction={onInteraction}
							smartLinkRenderer="embed"
						/>
					</SmartCardProvider>
				</Suspense>
			);
		}

		return null;
	},
);

const InteractiveFollowUpQuestions = React.memo(
	({
		segments,
		onFollowUpClick,
	}: {
		segments: FollowUpSegment[];
		onFollowUpClick?: (question: string) => void;
	}) => {
		const { sendAnalyticsEvent } = useAnalytics();

		useEffect(() => {
			sendAnalyticsEvent({
				eventType: 'screen',
				name: 'rovoChatActionableFollowUps',
			});
		}, [sendAnalyticsEvent]);

		if (segments.length === 0) {
			return null;
		}

		// The `stacked` experiment cohort normally returns questions with no surrounding
		// prose. Production responses can also include a lead-in followed by newline-
		// separated questions; those must use the same vertical layout so wrapped labels
		// stay aligned with their text rather than returning beneath the arrow icon.
		// True inline responses interleave sentence prose between questions.
		const hasProse = segments.some((segment) => segment.type === 'text');
		const firstSegment = segments[0];
		const isLineSeparatedList =
			firstSegment?.type === 'text' &&
			/\r?\n\s*$/.test(firstSegment.value) &&
			segments
				.slice(1)
				.every((segment) => segment.type === 'question' || segment.value.trim() === '');

		if (!hasProse || isLineSeparatedList) {
			const leadIn = isLineSeparatedList ? firstSegment.value.trim() : '';
			const questions = segments.filter((segment) => segment.type === 'question');

			return (
				<Stack space="space.100">
					{leadIn && <span>{leadIn}</span>}
					{questions.map((segment, index) => (
						<ActionableFollowUpLink
							key={index}
							testId={`actionable-follow-up-link-${index}`}
							ariaLabel={segment.value}
							onClick={() => onFollowUpClick?.(segment.value)}
						>
							{segment.value}
						</ActionableFollowUpLink>
					))}
				</Stack>
			);
		}

		let questionIndex = 0;
		return (
			<span>
				{segments.map((segment, index) => {
					if (segment.type === 'text') {
						return <React.Fragment key={index}>{segment.value}</React.Fragment>;
					}

					const linkIndex = questionIndex++;
					return (
						<ActionableFollowUpLink
							key={index}
							appearance="inline"
							testId={`actionable-follow-up-link-${linkIndex}`}
							ariaLabel={segment.value}
							onClick={() => onFollowUpClick?.(segment.value)}
						>
							{segment.value}
						</ActionableFollowUpLink>
					);
				})}
			</span>
		);
	},
);

const FollowUpQuestions = ({
	segments,
	onFollowUpClick,
	interactionMode,
}: {
	segments: FollowUpSegment[];
	onFollowUpClick?: (question: string) => void;
	interactionMode?: 'live' | 'read-only';
}): React.JSX.Element | null => {
	if (interactionMode === 'read-only') {
		return null;
	}

	return <InteractiveFollowUpQuestions segments={segments} onFollowUpClick={onFollowUpClick} />;
};

const MemoizedEmbedCard = React.memo(
	({
		url,
		sendAnalyticsEvent,
		conversationId,
		messageId,
	}: {
		url?: string;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		sendAnalyticsEvent: (event: any) => void;
		conversationId?: string;
		messageId?: string;
	}) => {
		return (
			<EmbedCardWithFallback
				url={url}
				onResolutionError={(failedUrl, errorInfo) => {
					sendAnalyticsEvent({
						action: 'failed',
						actionSubject: 'embedCard',
						actionSubjectId: 'urlResolution',
						attributes: {
							url: failedUrl,
							status: errorInfo?.status,
							errorMessage: errorInfo?.err?.message,
							conversationId,
							messageId,
						},
					});
				}}
			/>
		);
	},
);

function renderStatusComponent(
	props: StatusComponentProps,
	formatMessage: IntlShape['formatMessage'],
	getOnViewAnalysisClick: ((idx: number) => () => void) | undefined,
	onCitationClick:
		| ((index: number, citedProduct: string | null, product: string | null) => void)
		| undefined,
	markdownData: Record<string, string> | undefined,
	messageId: string | undefined,
	siteId: string,
	orgId: string,
	aaid: string,
	sourceProduct: string,
	tempIsThirdPartyAgent?: boolean,
	onFollowUpClick?: (question: string) => void,
	interactionMode?: 'live' | 'read-only',
	// Streaming-gated id used ONLY for the inline lumina path (undefined while
	// streaming). Kept separate from `messageId` so other status components (e.g.
	// charts) are unaffected by the gating.
	luminaMessageId?: string,
	onLuminaInteraction?: LuminaInteractionHandler,
) {
	if (props.localId.startsWith('data-sculptor-')) {
		return (
			<DataSculptorButton
				localId={props.localId}
				text={props.text}
				formatMessage={formatMessage}
				getOnViewAnalysisClick={getOnViewAnalysisClick}
			/>
		);
	}

	if (props.localId.startsWith('expandable-')) {
		const expandId = fg('rovo_chat_fix_status_localid_lookup')
			? props.localId.replace('expandable-', 'expand-')
			: `expand-${props.text.at(-1)}`;
		const expandDataContent = markdownData?.[expandId] ?? '';

		return (
			<ExpandableContent
				localId={props.localId}
				formatMessage={formatMessage}
				expandDataContent={expandDataContent}
				tempIsThirdPartyAgent={tempIsThirdPartyAgent}
			/>
		);
	}

	if (props.localId.startsWith('chart-')) {
		const chartId = fg('rovo_chat_fix_status_localid_lookup')
			? props.localId
			: `chart-${props.text.at(-1)}`;
		const isChartLoading = markdownData?.[`${chartId}-loading`] === 'true';
		const chartConfigJson = markdownData?.[chartId] ?? '{}';

		return (
			<ChartContent
				localId={props.localId}
				formatMessage={formatMessage}
				isChartLoading={isChartLoading}
				chartConfigJson={chartConfigJson}
				messageId={messageId}
				siteId={siteId}
			/>
		);
	}

	if (isInlineLuminaStatus(props.localId) && fg('rovo_chat_enable_inline_lumina')) {
		const luminaIndex = parseInt(props.text.replace('inline-lumina-', ''), 10);
		const luminaContent = markdownData?.[`lumina-${luminaIndex}`] ?? '';

		return (
			<InlineLuminaContent
				luminaContent={luminaContent}
				cloudId={siteId}
				orgId={orgId}
				requestingUserId={aaid}
				sourceProduct={sourceProduct}
				messageId={luminaMessageId}
				onInteraction={onLuminaInteraction}
			/>
		);
	}

	if (isActionableFollowUpsStatus(props.localId)) {
		const followUpsPayload = markdownData?.[props.localId] ?? '';
		// Parse the payload into ordered prose/question segments so the surrounding
		// text renders as normal stream text and each question renders as an inline
		// clickable link in place.
		const segments = parseActionableFollowUps(followUpsPayload);

		return (
			<FollowUpQuestions
				segments={segments}
				onFollowUpClick={onFollowUpClick}
				interactionMode={interactionMode}
			/>
		);
	}

	return <StatusCitation {...props} onClick={onCitationClick} />;
}

const InlineExtension = ({
	content,
	extensionType,
	extensionKey,
	parameters = {},
}: {
	content?: React.ReactNode;
	extensionType?: string;
	extensionKey?: string;
	parameters?: Record<string, unknown>;
}) => {
	if (
		extensionType &&
		extensionKey &&
		isSkillTagExtension(extensionType, extensionKey) &&
		parameters?.slug
	) {
		return (
			<Box
				as="span"
				xcss={skillsStyles.container}
				// editor adds margin around inlineExtensions but Renderer does not
				style={{ margin: `1px 1px ${token('space.050')}` }}
				// Data attributes for clipboard copy-paste support
				data-extension-type={ROVO_SKILL_EXTENSION_TYPE}
				data-extension-key={ROVO_SKILL_COMBINED_EXTENSION_KEY}
				data-parameters={JSON.stringify(parameters)}
			>
				<SkillTag invertColors {...(parameters as Skill)} />
			</Box>
		);
	}
	return content;
};

const PassthroughExtension = ({ content }: ExtensionComponentProps): React.ReactNode => content;

const LuminaLayout = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian-internal_lumina-layout" */
		'@atlassian/lumina'
	).then((mod) => ({ default: mod.LuminaLayout })),
);

/**
 * This Renderer is intended to support the full set of markdown which we have
 * added support to our markdown transformers for.
 *
 * It is not currently lazy loaded -- we may want to do this in future to allow
 * products to have smaller bundles for Edit views. This was not needed at time
 * of writing, as the only view mode on confluence, which already includes the Renderer
 * in its initial js load.
 */
/**
 * Check if a URL is an authentication redirect URL that requires special handling
 * Auth URLs need to open without 'noopener' to allow OAuth callback communication
 */
const isAuthRedirectUrl = (url: string): { isForgeAuth: boolean; is3pAuth: boolean } => {
	const isForgeAuth =
		(url.startsWith('https://id.stg.internal.atlassian.com/login') ||
			url.startsWith('https://id.atlassian.com/login')) &&
		url.includes('outboundAuth%2Fstart');

	const is3pAuth =
		url.startsWith('https://id.stg.internal.atlassian.com/outboundAuth') ||
		url.startsWith('https://id.atlassian.com/outboundAuth');

	return { isForgeAuth, is3pAuth };
};

export function ContentRenderer({
	content,
	onContentSelected,
	onContentCopied,
	lightweightEncoding = false,
	onResponseLinkClick,
	onSmartCardClick,
	onCodeBlockCopied,
	allowDownloadCodeBlock = false,
	onCitationClick,
	onAuthLinkClick,
	getOnViewAnalysisClick,
	onDeepLinkClick,
	markdownData,
	smallerFont = false,
	largerFont = false,
	boldText = false,
	subtlestText = false,
	productKey,
	orgId,
	siteId,
	aaid,
	conversationId,
	theme = 'default',
	messageId,
	contentStillStreaming,
	shouldOverrideCardAsInline = false,
	isExperimentEnabled,
	adfTransformer,
	sendLinkCountMetric,
	extensionComponent: Extension = PassthroughExtension,
	xpcWrapUrl,
	tempIsThirdPartyAgent,
	onFollowUpClick,
	interactionMode,
	onLuminaInteraction,
	respondedAgentId,
}: ContentRendererProps): JSX.Element {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const currentValuesRef = React.useRef({
		formatMessage,
		getOnViewAnalysisClick,
		onCitationClick,
		markdownData,
		onFollowUpClick,
		interactionMode,
	});

	// CRCS-5217: refresh synchronously during render so StatusComponentMemoized
	// reads this render's markdownData, not the prior commit's snapshot.
	if (fg('rovo_chat_fix_status_component_stale_markdowndata')) {
		currentValuesRef.current = {
			formatMessage,
			getOnViewAnalysisClick,
			onCitationClick,
			markdownData,
			onFollowUpClick,
			interactionMode,
		};
	}

	React.useEffect(() => {
		if (fg('rovo_chat_fix_status_component_stale_markdowndata')) {
			return;
		}
		currentValuesRef.current = {
			formatMessage,
			getOnViewAnalysisClick,
			onCitationClick,
			markdownData,
			onFollowUpClick,
			interactionMode,
		};
	}, [
		formatMessage,
		getOnViewAnalysisClick,
		onCitationClick,
		markdownData,
		onFollowUpClick,
		interactionMode,
	]);

	// The messageId is only stable/final once streaming completes: while streaming,
	// the assistant message is held under a temporary id and only swapped to the
	// final `message.message.id` on the final response. The inline lumina analytics
	// join on this id against the "extracted" event which always uses the final id,
	// so we must not surface the temporary id there.
	const luminaMessageId = contentStillStreaming ? undefined : messageId;

	// Without this, components in StatusComponent will mount/unmount on every new stream part
	// which makes them non-interactable and causes charts to flicker due to animation on mount
	const StatusComponentMemoized = React.useMemo(() => {
		return (props: StatusComponentProps) => {
			const {
				formatMessage,
				getOnViewAnalysisClick,
				onCitationClick,
				markdownData,
				onFollowUpClick,
				interactionMode,
			} = currentValuesRef.current;
			return renderStatusComponent(
				props,
				formatMessage,
				getOnViewAnalysisClick,
				onCitationClick,
				markdownData,
				messageId,
				siteId,
				orgId ?? '',
				aaid ?? '',
				productKey,
				tempIsThirdPartyAgent,
				onFollowUpClick,
				interactionMode,
				luminaMessageId,
				onLuminaInteraction,
			);
		};
	}, [
		messageId,
		luminaMessageId,
		siteId,
		orgId,
		aaid,
		productKey,
		tempIsThirdPartyAgent,
		onLuminaInteraction,
	]);

	const globalTheme = useThemeObserver();
	const isDarkMode = globalTheme.colorMode === 'dark';
	const { value: mentionResourceOverride } = useRovoMentionProvider();
	const { value: profilecardResourceOverride } = useRovoProfilecardProvider();
	const userProfileHref = React.useCallback(
		(userId: string) =>
			navigateToTeamsApp({
				type: 'USER',
				payload: { userId },
				cloudId: siteId,
			}).href,
		[siteId],
	);
	const rendererRef = React.useRef<HTMLDivElement>(null);
	// Single ref object to track streaming render times for averaging
	const streamingDataRef = React.useRef({
		renderStartTime: null as number | null,
		metricStartTime: null as number | null,
		contentStreamed: false,
		runningAvgRenderTime: 0,
		renderCount: 0,
		lastContent: null as Content | null,
	});

	if (streamingDataRef.current.lastContent !== content) {
		// For streaming content, track start time for individual renders
		const contentRenderStartTime = performance.now();
		streamingDataRef.current.renderStartTime = contentRenderStartTime;
		streamingDataRef.current.lastContent = content;
		// if it did not stream before at all or the metric has not started yet, start a new metric log
		if (
			!streamingDataRef.current.contentStreamed ||
			streamingDataRef.current.metricStartTime == null
		) {
			streamingDataRef.current.metricStartTime = contentRenderStartTime;
			timeToRenderMessageContent.start({ startTime: contentRenderStartTime });
		}

		if (contentStillStreaming) {
			streamingDataRef.current.contentStreamed = contentStillStreaming;
		}
	}

	// TODO: THIS MUST BE CORRECTED!
	// Strictly temporary fix to ensure citations are clickable once they appear for the user
	// Sources are only available after the final response, and even though onCitationClick changes
	// when this updates, the PureComponent of ReactRenderer does not re-render since none of its props have changed
	// This solution will get the job done for now to satisfy stakeholders, but we should improve it ASAP
	const [rendererKey, setRendererKey] = React.useState(0);

	React.useEffect(
		() => {
			setRendererKey(rendererKey + 1);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[lightweightEncoding, xpcWrapUrl],
	);

	// Hack for custom font sizes for the renderer
	// https://www.figma.com/design/rikAa0DTMVNjS5Ud1IuIAi/Deep-research?node-id=174-33296&t=0YpJzbIMBkAuiyt3-0
	// Standard font size is 14px; smallerFont uses 0.75rem (12px), largerFont uses 1rem (16px)
	React.useEffect(() => {
		if (rendererRef.current) {
			if (smallerFont) {
				rendererRef.current.style.fontSize = '0.75rem';
			} else if (largerFont) {
				rendererRef.current.style.fontSize = '1rem';
			}
		}
	}, [rendererRef, smallerFont, largerFont, rendererKey]);

	const mentionResource: Providers['mentionProvider'] = React.useMemo(
		() =>
			Promise.resolve(
				mentionResourceOverride ||
					new SmartMentionResource({
						productKey,
						siteId,
						includeTeams: true,
						includeUsers: true,
						shouldHighlightMention(mention) {
							return mention.id === aaid;
						},
					}),
			),
		[productKey, siteId, aaid, mentionResourceOverride],
	);

	const profileCardProvider: Providers['profilecardProvider'] = React.useMemo(
		() =>
			Promise.resolve(
				profilecardResourceOverride || {
					cloudId: siteId,
					resourceClient: new ProfileCardClient({
						url: '/gateway/api/directory/graphql',
						cloudId: siteId,
						productIdentifier: productKey,
					}),
					getActions: (id: string) => [
						{
							label: formatMessage(messages.profileCardViewProfileButton),
							id: 'view-profile',
							callback: () =>
								window.open(
									fg('platform-adopt-teams-nav-config') ? userProfileHref(id) : `/people/${id}`,
									'_blank',
									'noopener, noreferrer',
								),
						},
					],
					...(fg('people-teams_migrate-user-profile-card')
						? {
								renderUserMentionCard: ({
									userId,
									cloudId,
									children,
								}: {
									userId: string;
									cloudId: string;
									children: React.ReactNode;
								}) => (
									<UserProfileCardForMention
										userId={userId}
										cloudId={cloudId}
										orgId={orgId ?? ''}
										viewerId={aaid ?? ''}
									>
										{children}
									</UserProfileCardForMention>
								),
							}
						: {}),
				},
			),
		[siteId, productKey, formatMessage, profilecardResourceOverride, userProfileHref, orgId, aaid],
	);

	const dataProviders = React.useMemo(
		() =>
			ProviderFactory.create({
				mentionProvider: mentionResource,
				profilecardProvider: profileCardProvider,
			}),
		[mentionResource, profileCardProvider],
	);

	const handleSelection = () => {
		const selection = window.getSelection();

		if (onContentSelected && selection?.toString()) {
			onContentSelected();
		}
	};

	const handleRenderComplete = React.useCallback(
		(stat: RenderOutputStat) => {
			const { renderStartTime, lastContent } = streamingDataRef.current;

			// Only process render completion if we have a valid start time and content matches
			// This handles race conditions where new content arrives before previous render completes
			if (
				renderStartTime === null ||
				lastContent !== content ||
				streamingDataRef.current.metricStartTime === null
			) {
				return;
			}

			const renderTime = performance.now() - renderStartTime;

			if (contentStillStreaming) {
				// For streaming content, update running average
				streamingDataRef.current.renderCount++;

				// Calculate running average: new_avg = old_avg + (new_value - old_avg) / count
				streamingDataRef.current.runningAvgRenderTime =
					streamingDataRef.current.runningAvgRenderTime +
					(renderTime - streamingDataRef.current.runningAvgRenderTime) /
						streamingDataRef.current.renderCount;

				// Don't emit metric yet, wait for streaming to complete
			} else {
				if (streamingDataRef.current.contentStreamed) {
					// Streaming has completed, include final render time and emit metric
					streamingDataRef.current.renderCount++;
					streamingDataRef.current.runningAvgRenderTime =
						streamingDataRef.current.runningAvgRenderTime +
						(renderTime - streamingDataRef.current.runningAvgRenderTime) /
							streamingDataRef.current.renderCount;

					timeToRenderMessageContent.stop({
						customData: {
							...stat,
							renderTime: streamingDataRef.current.runningAvgRenderTime,
							totalStreamingUpdates: streamingDataRef.current.renderCount,
						},
						// ovverride to have the avg render time across all render cycles instead the actual render time per render cycle
						stopTime:
							streamingDataRef.current.metricStartTime +
							streamingDataRef.current.runningAvgRenderTime,
					});
				} else {
					// Non-streaming content, emit metric normally
					timeToRenderMessageContent.stop({ customData: { ...stat, totalStreamingUpdates: 0 } });
				}

				// Reset streaming state
				streamingDataRef.current.renderStartTime = null;
				streamingDataRef.current.metricStartTime = null;
				streamingDataRef.current.runningAvgRenderTime = 0;
				streamingDataRef.current.renderCount = 0;
				streamingDataRef.current.contentStreamed = false;
			}
		},
		[contentStillStreaming, content],
	);

	React.useEffect(() => {
		(async () => {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			await hydrateLinkingPlatformCache(localStorage);
		})();
	}, []);

	// Inline extension component that handles skill tags (via InlineExtension) and delegates
	// all other types to extensionComponent. This allows consumers to render custom inline
	// extensions (e.g. redaction scribbles) by providing an extensionComponent prop.
	const DelegatingInlineExtension = React.useCallback(
		({
			content,
			extensionType,
			extensionKey,
			parameters = {},
		}: {
			content?: React.ReactNode;
			extensionType?: string;
			extensionKey?: string;
			parameters?: Record<string, unknown>;
		}) => {
			if (
				extensionType &&
				extensionKey &&
				isSkillTagExtension(extensionType, extensionKey) &&
				parameters?.slug
			) {
				return (
					<InlineExtension
						content={content}
						extensionType={extensionType}
						extensionKey={extensionKey}
						parameters={parameters}
					/>
				);
			}
			return (
				<Extension
					content={content}
					extensionType={extensionType}
					extensionKey={extensionKey}
					parameters={parameters}
				/>
			);
		},
		[Extension],
	);

	// See https://product-fabric.atlassian.net/wiki/spaces/EUXQ/pages/3523842928/Output+formatting+support+openai+-+editor+content
	// for content we support transforming from markdown returned by OpenAI into editor content.
	const docContent = React.useMemo(() => {
		const adf = encodeAdf(
			content,
			lightweightEncoding,
			isExperimentEnabled,
			shouldOverrideCardAsInline,
			undefined,
			xpcWrapUrl,
		);
		// Apply optional ADF transformer if provided
		return adfTransformer ? adfTransformer(adf) : adf;
	}, [
		content,
		lightweightEncoding,
		isExperimentEnabled,
		shouldOverrideCardAsInline,
		adfTransformer,
		xpcWrapUrl,
	]);

	const disabledAgentMentionTooltip = respondedAgentId
		? formatMessage(messages.disabledAgentMentionTooltip)
		: '';

	const linkCountMetricSentRef = React.useRef(false);
	// Track if we ever saw the message streaming - if not, it's a historical message
	const wasEverStreamingRef = React.useRef(contentStillStreaming);

	React.useEffect(() => {
		// Update tracking if we see streaming state
		if (contentStillStreaming) {
			wasEverStreamingRef.current = true;
		}
	}, [contentStillStreaming]);

	React.useEffect(() => {
		// Only send the metric once when streaming is complete AND the message was actually streamed
		// (not loaded from history where contentStillStreaming was always false)
		if (
			!contentStillStreaming &&
			!linkCountMetricSentRef.current &&
			sendLinkCountMetric &&
			wasEverStreamingRef.current
		) {
			linkCountMetricSentRef.current = true;
			sendLinkCountMetric(true, docContent);
		}
	}, [contentStillStreaming, docContent, sendLinkCountMetric]);

	const onSetLinkTargetCallback = React.useCallback(
		(url: string) => handleOnSetLinkTarget(url, productKey),
		[productKey],
	);

	const nodeComponents = React.useMemo(
		() => ({
			// Node override for `status` to enable citation clicks and data sculptor
			status: StatusComponentMemoized,
			inlineExtension: DelegatingInlineExtension,
			extension: Extension,
			embedCard: ({ url, _layout = 'center' }: { url?: string; _layout?: string } = {}) => {
				return (
					<MemoizedEmbedCard
						url={url}
						sendAnalyticsEvent={sendAnalyticsEvent}
						conversationId={conversationId}
						messageId={messageId}
					/>
				);
			},
			// MentionNodeComponent is a stable module-level component, so the `mention`
			// override keeps a constant type across renders (no per-mention remount).
			...(respondedAgentId ? { mention: MentionNodeComponent } : {}),
		}),
		[
			StatusComponentMemoized,
			DelegatingInlineExtension,
			Extension,
			sendAnalyticsEvent,
			conversationId,
			messageId,
			respondedAgentId,
		],
	);

	const onClickCapture = React.useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (!onCodeBlockCopied) {
				return;
			}
			/**
			 * This is a hack to find out if codeblock inside renderer is clicked
			 * We find up to 3 parents level up whether the clicked element is the code block copy button
			 * Renderer does not support custom event handlers for code block copy button
			 * (in https://atlassian.slack.com/archives/CFG3PSQ9E/p1717651112836979?thread_ts=1717648170.958499&cid=CFG3PSQ9E)
			 * and AnalyticsContext doesn't work for this use case too
			 */
			let targetElement: HTMLElement | null | undefined = event.target as HTMLElement;
			for (let i = 0; i < 3; i++) {
				if (!targetElement) {
					return;
				}

				if (targetElement.classList.contains('copy-to-clipboard')) {
					onCodeBlockCopied?.();
					return;
				}

				targetElement = targetElement?.parentElement;
			}
		},
		[onCodeBlockCopied],
	);

	const rendererContent = (
		<Box
			xcss={cx(
				subtlestText && stylesOverrides.subtlestText,
				boldText && stylesOverrides.boldText,
				theme === 'inverse' && stylesOverrides.inverse,
				theme === 'inverse' && isDarkMode && stylesOverrides.inverseDarkMode,
			)}
			onMouseUp={handleSelection}
			onCopy={onContentCopied}
			onClickCapture={onClickCapture}
		>
			<ReactRenderer
				innerRef={rendererRef}
				key={rendererKey}
				onComplete={handleRenderComplete}
				document={docContent}
				media={{
					// We don't currently support images with captions
					allowLinking: false,
				}}
				allowCopyToClipboard
				allowWrapCodeBlock
				allowDownloadCodeBlock={allowDownloadCodeBlock}
				onSetLinkTarget={onSetLinkTargetCallback}
				dataProviders={dataProviders}
				nodeComponents={nodeComponents}
				eventHandlers={{
					smartCard: {
						onClick: (event, url) => {
							onSmartCardClick && onSmartCardClick();
							event.preventDefault();
							if (!url) {
								return;
							}
							const redirectUrl = transformIfConfluenceLink(url, true);

							// Check if it's an auth redirect URL
							// This is a URL returned when forge app returned with `USER_CONSENT_REQUIRED`
							// https://product-fabric.atlassian.net/browse/ROVOFE-11
							const { isForgeAuth: isForgeAuthRedirect, is3pAuth: is3pAuthRedirect } =
								isAuthRedirectUrl(redirectUrl);

							// Forge newly opened window need access to `window.opener` to communicate back to this window
							if (isForgeAuthRedirect || is3pAuthRedirect) {
								onAuthLinkClick?.(redirectUrl, is3pAuthRedirect ? '3p' : 'forge');
								window.open(redirectUrl, '_blank');
							} else if (
								shouldHandleDeepLink({
									url: redirectUrl,
									onDeepLinkClick,
								})
							) {
								handleDeepLink({
									url: redirectUrl,
									productKey,
									onDeepLinkClick,
									conversationId,
								});
							} else {
								window.open(redirectUrl, '_blank', 'noopener, noreferrer');
							}
						},
					},
					link: {
						onClick: (event, url) => {
							onResponseLinkClick && onResponseLinkClick();
							event.preventDefault();
							if (!url) {
								return;
							}
							const redirectUrl = transformIfConfluenceLink(url, true);

							// This is a URL returned when forge app returned with `USER_CONSENT_REQUIRED`
							// https://product-fabric.atlassian.net/browse/ROVOFE-11
							const { isForgeAuth: isForgeAuthRedirect, is3pAuth: is3pAuthRedirect } =
								isAuthRedirectUrl(redirectUrl);

							// Forge newly opened window need access to `window.opener` to communicate back to this window
							if (isForgeAuthRedirect || is3pAuthRedirect) {
								onAuthLinkClick?.(redirectUrl, is3pAuthRedirect ? '3p' : 'forge');
								window.open(redirectUrl, '_blank');
							} else if (
								shouldHandleDeepLink({
									url: redirectUrl,
									onDeepLinkClick,
								})
							) {
								handleDeepLink({
									url: redirectUrl,
									productKey,
									onDeepLinkClick,
									conversationId,
								});
							} else {
								window.open(redirectUrl, '_blank', 'noopener, noreferrer');
							}
						},
					},
				}}
			/>
		</Box>
	);

	if (respondedAgentId) {
		return (
			<MentionNodeComponentProvider
				respondedAgentId={respondedAgentId}
				disabledTooltip={disabledAgentMentionTooltip}
			>
				{rendererContent}
			</MentionNodeComponentProvider>
		);
	}

	return rendererContent;
}

// Assumes expandData header and content are separated by a newline
function splitHeaderAndContent(expandData: string): { header: string; content: string } {
	const [header, ...rest] = expandData.split(/\r?\n/);
	return {
		header,
		content: rest.join('\n'),
	};
}
